/**
 * The decisions of a tooltip, with no DOM in them: what each event of the trigger does, when
 * the open waits for the delay, and when an open tooltip has lost its hover. `Tooltip` feeds the
 * events and the facts of the moment in, and reacts to the state that comes out.
 */

import {
    type TooltipEvent, TooltipEventKind, type TooltipInput, TooltipMode, type TooltipState,
    type TooltipWindow,
} from '@/types/tooltip';

export const INITIAL_TOOLTIP_STATE: TooltipState = {
    isOpen: false,
    isShown: false,
    isPointerDown: false,
    hasPointerMoveOpened: false,
    isTimerRunning: false,
};

interface OpenTooltip {
    id: string;
    close: () => void;
}

/** Within `skipMs` of a close, and while a tooltip is open, the next one opens at once. */
export function createTooltipWindow(skipMs: number): TooltipWindow {
    let openTooltip: OpenTooltip | null = null;
    let lastClosedAt: number | null = null;
    return {
        isOpenDelayed: (now: number): boolean =>
            openTooltip === null && (lastClosedAt === null || now - lastClosedAt >= skipMs),
        markOpen: (tooltipId: string, closeTooltip: () => void): void => {
            openTooltip?.close();
            openTooltip = { id: tooltipId, close: closeTooltip };
        },
        markClosed: (tooltipId: string, now: number): void => {
            if (openTooltip?.id === tooltipId) {
                openTooltip = null;
            }
            lastClosedAt = now;
        },
    };
}

function getOpenState(state: TooltipState, input: TooltipInput): TooltipState {
    if (state.isOpen || !input.isHoveredOrFocused()) {
        return { ...state, isTimerRunning: false };
    }
    return { ...state, isOpen: true, isShown: true, isTimerRunning: false };
}

function getClosedState(state: TooltipState): TooltipState {
    return { ...state, isOpen: false, isTimerRunning: false };
}

export function reduceTooltip(
    state: TooltipState, event: TooltipEvent, input: TooltipInput,
): TooltipState {
    const isHover = input.mode === TooltipMode.Hover;
    if (input.mode === TooltipMode.Inert) {
        return state.isOpen || state.isTimerRunning ? getClosedState(state) : state;
    }
    switch (event.kind) {
        case TooltipEventKind.PointerEnter:
            // The pointer is on the trigger, so the hover check has nothing to add.
            return isHover || state.isOpen
                ? state
                : { ...state, isOpen: true, isShown: true, isTimerRunning: false };
        case TooltipEventKind.PointerMove: {
            if (isHover === false || event.isTouch || state.hasPointerMoveOpened) {
                return state;
            }
            const movedState = { ...state, hasPointerMoveOpened: true };
            if (state.isOpen) {
                return movedState;
            }
            return input.isOpenDelayed
                ? { ...movedState, isTimerRunning: true }
                : getOpenState(movedState, input);
        }
        case TooltipEventKind.PointerLeave:
            return getClosedState({ ...state, hasPointerMoveOpened: false });
        case TooltipEventKind.PointerDown:
            return isHover ? getClosedState({ ...state, isPointerDown: true }) : state;
        case TooltipEventKind.PointerUp:
            return { ...state, isPointerDown: false };
        case TooltipEventKind.Focus:
            return isHover && !state.isPointerDown ? getOpenState(state, input) : state;
        case TooltipEventKind.Blur:
        case TooltipEventKind.Click:
            return isHover ? getClosedState(state) : state;
        case TooltipEventKind.TimerFired:
            return getOpenState(state, input);
        case TooltipEventKind.HoverCheck:
            return state.isOpen && !input.isHoveredOrFocused() ? getClosedState(state) : state;
        case TooltipEventKind.OutsideClose:
            return getClosedState(state);
        case TooltipEventKind.ContentRemoved:
            return { ...state, isShown: false };
        case TooltipEventKind.ModeChanged:
            return isHover ? state : getOpenState(state, input);
        default:
            return state;
    }
}
